import {
  Component,
  OnInit,
  ViewEncapsulation,
  inject,
  signal,
  effect,
  computed,
  CUSTOM_ELEMENTS_SCHEMA
} from '@angular/core';

import { Router, NavigationEnd, NavigationStart, NavigationCancel, NavigationError, RouterModule } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { filter } from 'rxjs/operators';
import { CoreService } from '../../core/services/core.service';
import { TranslateModule } from '@ngx-translate/core';

import { AppSidebar } from '../../shared/components/app-sidebar/app-sidebar';
import { AppNavbar } from '../../shared/components/app-navbar/app-navbar';
import { AppNavItemComponent } from '../../shared/components/app-nav-item/app-nav-item';
import { NavigationService } from '../../core/services/navigation.service';
import { ModuleService } from '../../shared/services/module.service';
import { BusinessContextService } from '../../core/services/business-context.service';
import { LoadingService } from '../../core/services/loading.service';
import { AppConfirmDialog } from '../../shared/components/app-confirm-dialog/app-confirm-dialog';
import { AuthService } from '../../core/services/auth.service';
import { DialogComponent } from '../../shared/components/app-dialog/app-dialog';


const MOBILE_VIEW = 'screen and (max-width: 768px)';
const TABLET_VIEW = 'screen and (min-width: 769px) and (max-width: 1024px)';

@Component({
  selector: 'app-admin-layout',
  standalone: true,
  imports: [
    RouterModule,
    CommonModule,
    TranslateModule,
    AppSidebar,
    AppNavbar,
    AppNavItemComponent,
    AppConfirmDialog,
    DialogComponent,
  ],

  templateUrl: './admin-layout.html',
  styleUrls: ['./admin-layout.css'],
  encapsulation: ViewEncapsulation.None,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AdminLayout implements OnInit {
  private router = inject(Router);
  private settings = inject(CoreService);
  private navService = inject(NavigationService);
  private moduleService = inject(ModuleService);
  private bizCtx = inject(BusinessContextService);
  private breakpointObserver = inject(BreakpointObserver);
  private authService = inject(AuthService);
  private loadingService = inject(LoadingService);

  showLogoutDialog = false;

  confirmLogout() {
    this.showLogoutDialog = false;
    this.authService.logout();
  }

  isBlocked = computed(() => this.bizCtx.isBlocked());
  isNoPlan = computed(() => this.bizCtx.isNoPlan());
  isSuperAdmin = computed(() => this.bizCtx.isSuperAdmin());
  isMembershipPage = signal(false);

  private checkMembershipPage(url: string) {
    this.isMembershipPage.set(url.includes('/membership') || url.includes('/my-membership'));
  }


  groupedMenu = this.navService.groupedMenu;
  options = this.settings.getOptions();

  footerItems = computed(() => {
    const items = [
      { displayName: 'Soporte', icon: 'streamline-plump:customer-support-3', route: '/app/dashboard' },
      { displayName: 'Tutoriales', icon: 'ph:video', route: '/app/dashboard' },
    ];
    
    if (!this.bizCtx.isSuperAdmin()) {
      items.push({ displayName: 'Negocios', icon: 'solar:buildings-outline', route: '/app/select-business' });
    }
    
    return items;
  });

  currentYear = new Date().getFullYear();

  sidenavOpened = signal(true);
  sidenavCollapsed = signal(this.options.sidenavCollapsed);
  isOver = signal(false);
  toggleSidebarCollapse() {
    this.sidenavCollapsed.update(v => !v);
  }


  readonly syncLayoutOptions = effect(() => {
    this.settings.setOptions({
      ...this.options,
      sidenavCollapsed: this.sidenavCollapsed(),
      sidenavOpened: this.sidenavOpened()
    });
  });
  private loadingTimer: any;

  ngOnInit(): void {
    if (this.moduleService.allModules().length === 0) {
      this.moduleService.initSidebarModules().subscribe();
    }

    this.checkMembershipPage(this.router.url);

    // Sincronizar contexto de negocio siempre para evitar datos obsoletos (status, perms)
    // Pero solo si no estamos en la página de selección
    const isSelectPage = this.router.url.includes('select-business');
    const isDashboard = this.router.url.includes('/app/dashboard');

    if (!isSelectPage) {
      this.authService.getMyBusinesses().subscribe((response: any) => {
        const isSA = response.isSuperAdmin;

        // Regla de Oro: El SuperAdmin NUNCA mantiene contexto de negocio al recargar o iniciar
        // Siempre debe iniciar en Modo Plataforma (Panel Global)
        if (isSA) {
          this.bizCtx.clearContext();
          return;
        }

        // Para usuarios regulares, mantener contexto activo si existe
        const activeId = this.bizCtx.activeBusiness()?.business;
        // El backend devuelve el ID en 'business' para usuarios regulares y '_id' para SuperAdmin
        const currentBiz = response.businesses?.find((b: any) => {
          const bId = b.business || b._id || b.id;
          return bId === activeId;
        });
        
        if (currentBiz) {
          this.bizCtx.selectFromRaw(currentBiz, response.isSuperAdmin);
        } else {
          // Si no hay negocio activo, intentar auto-selección
          const selected = this.bizCtx.autoSelectIfSingle(response.businesses || [], response.isSuperAdmin);
          
          // Si no pudo auto-seleccionar y no estamos en una página permitida sin contexto, ir a selección
          if (!selected && !this.bizCtx.hasContext() && !isSelectPage) {
            this.router.navigate(['/app/select-business'], { queryParams: { auto: true } });
          }
        }
      });
    }



    this.breakpointObserver
      .observe([MOBILE_VIEW, TABLET_VIEW])
      .subscribe(state => {
        this.isOver.set(state.breakpoints[MOBILE_VIEW]);
        this.sidenavOpened.set(!state.breakpoints[MOBILE_VIEW]);

        if (!this.sidenavCollapsed()) {
          this.sidenavCollapsed.set(state.breakpoints[TABLET_VIEW]);
        }
      });

    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        // Peformance Hack: Añadir un pequeño retraso antes de mostrar el spinner
        // Si la navegación es rápida (<300ms), el usuario no ve el parpadeo del "Cargando"
        // y la app se siente instantánea.
        clearTimeout(this.loadingTimer);
        this.loadingTimer = setTimeout(() => {
          this.loadingService.showLoading('Navegando', 'Cargando vista...');
        }, 300);
        this.checkMembershipPage(event.url);
      } else if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
        clearTimeout(this.loadingTimer);
        this.loadingService.hideLoading();

        if (event instanceof NavigationEnd) {
          this.checkMembershipPage(event.url);
        }

        const el = document.querySelector('#layout-content');
        if (el) el.scrollTo({ top: 0 });

        if (this.isOver()) {
          this.sidenavOpened.set(false);
        }
      }
    });
  }

  toggleCollapsed() {
    this.sidenavOpened.update(open => !open);
  }

  onFooterItemClick(item: any) {
    if (item.route === '/app/select-business') {
      this.bizCtx.clearContext();
    }

    if (item.externalLink) {
      window.open(item.route, '_blank');
    } else {
      this.router.navigate([item.route]);
    }
  }

}